import { useEffect, useRef, useState, useCallback } from 'react';

export const useInfiniteScroll = (loadMore: () => void) => {
  const [page, setPage] = useState(1);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleLoadMore = useCallback(async () => {
    if (!canLoadMore || isLoading) return;

    setIsLoading(true);
    try {
      await loadMore();
    } finally {
      setIsLoading(false);
    }
  }, [canLoadMore, isLoading, loadMore]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && canLoadMore && !isLoading) {
            setPage(prevPage => prevPage + 1);
            handleLoadMore();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observerRef.current.observe(currentRef);
    }

    return () => {
      if (currentRef && observerRef.current) {
        observerRef.current.unobserve(currentRef);
      }
    };
  }, [canLoadMore, isLoading, handleLoadMore]);

  return {
    page,
    setPage,
    canLoadMore,
    setCanLoadMore,
    loadMoreRef,
    isLoading
  };
};
